import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../api/axios';
import {
  Loader2, RefreshCw, Download, AlertTriangle, Info, Star, ArrowUp, ArrowDown, Minus,
  FileText, ChevronDown, Search, Check,
} from 'lucide-react';

/**
 * GbpScoreMatrix — lokasyon × ay puan matrisi
 *
 * Rapor sekmesindeki günlük/aylık tablodan farkı: orası iki gecelik fotoğrafın
 * farkına dayanır, bu yüzden ancak fotoğraf çekmeye başladığımız günden ileri
 * gider. Buradaki her hücre o ay GELEN yorumların ortalamasıdır ve yorumların
 * kendi tarihinden hesaplandığı için geçmiş aylar da doludur.
 *
 * Hücre ile genel skor AYNI ŞEY DEĞİL: genel skor Google'ın gösterdiği kümülatif
 * puan (binlerce yorumun ağırlığıyla neredeyse hiç kıpırdamaz), hücre ise o ayın
 * kendi karnesi. Ofisin son dönemde iyiye mi kötüye mi gittiği ancak hücrelerden
 * okunur — arayüz bu ayrımı yazıyla söylemek zorunda.
 */

const nfmt = new Intl.NumberFormat('tr-TR');
const MONTHS_SHORT = ['OCA', 'ŞUB', 'MAR', 'NİS', 'MAY', 'HAZ', 'TEM', 'AĞU', 'EYL', 'EKİ', 'KAS', 'ARA'];
const MONTHS_LONG = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const monthShort = (k) => {
  const [y, m] = k.split('-');
  return `${MONTHS_SHORT[Number(m) - 1]} '${y.slice(2)}`;
};
const monthLong = (k) => {
  const [y, m] = k.split('-');
  return `${MONTHS_LONG[Number(m) - 1]} ${y}`;
};

const r1 = (v) => (v == null ? '—' : v.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const r2 = (v) => (v == null ? '—' : v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/**
 * Renk skalası müşterinin gönderdiği örnekle birebir. Eşikler puanın kendisine
 * göre, yorum sayısına göre DEĞİL — tek yorumlu bir ay da 5,0 ise koyu yeşil
 * olur; o yüzden az yorumlu hücre ayrıca soluk gösterilir (bkz. `thin`).
 */
const scale = (v) => {
  if (v == null) return { cls: 'text-gray-300', label: 'Veri yok' };
  if (v >= 4.95) return { cls: 'bg-emerald-700 text-white', label: 'Mükemmel' };
  if (v >= 4.45) return { cls: 'bg-emerald-100 text-emerald-900', label: 'Çok iyi' };
  if (v >= 2.95) return { cls: 'bg-amber-100 text-amber-900', label: 'Orta' };
  if (v >= 1.95) return { cls: 'bg-orange-200 text-orange-900', label: 'Zayıf' };
  return { cls: 'bg-rose-500 text-white', label: 'Çok zayıf' };
};

/** PDF hücre renkleri: `scale()` ile aynı eşikler, Tailwind tonlarının RGB karşılığı. */
const pdfScale = (v) => {
  if (v == null) return { fill: null, text: [203, 213, 225] };
  if (v >= 4.95) return { fill: [4, 120, 87], text: [255, 255, 255] };
  if (v >= 4.45) return { fill: [209, 250, 229], text: [6, 78, 59] };
  if (v >= 2.95) return { fill: [254, 243, 199], text: [120, 53, 15] };
  if (v >= 1.95) return { fill: [254, 215, 170], text: [124, 45, 18] };
  return { fill: [244, 63, 94], text: [255, 255, 255] };
};

/** jsPDF'in gömülü Helvetica'sı ş/ğ/İ basamıyor; Inter'i ilk PDF'te bir kez yükleyip saklıyoruz. */
let interCache = null;
const toB64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};
async function loadInter() {
  if (!interCache) {
    interCache = Promise.all(['/fonts/Inter-Regular.ttf', '/fonts/Inter-SemiBold.ttf'].map(async (u) => {
      const r = await fetch(u);
      if (!r.ok) throw new Error(`Font yüklenemedi: ${u}`);
      return toB64(await r.arrayBuffer());
    })).catch((e) => { interCache = null; throw e; });
  }
  return interCache;
}

/**
 * Seçili ofislerin TOPLAM satırı. Tüm ofisler seçiliyken backend'in toplamı kullanılır;
 * alt kümede aynı mantıkla yeniden hesaplanır: aylık puan = o ay gelen tüm yorumların
 * ortalaması (yorum sayısıyla ağırlıklı), genel skor = yorum sayısıyla ağırlıklı puan.
 */
function subsetTotal(list) {
  const months = {};
  let rw = 0; let rc = 0; let reviewCount = 0; let missing = 0;
  for (const l of list) {
    if (l.missing) { missing += 1; continue; }
    reviewCount += l.reviewCount || 0;
    if (l.rating != null && l.reviewCount) { rw += l.rating * l.reviewCount; rc += l.reviewCount; }
    for (const [m, c] of Object.entries(l.months || {})) {
      if (!c || !c.reviews) continue;
      const t = months[m] || (months[m] = { sum: 0, reviews: 0 });
      t.sum += c.avg * c.reviews;
      t.reviews += c.reviews;
    }
  }
  const out = {};
  for (const [m, t] of Object.entries(months)) out[m] = { avg: t.sum / t.reviews, reviews: t.reviews };
  return {
    rating: rc ? rw / rc : null,
    reviewCount,
    months: out,
    locations: list.length - missing,
    missing,
  };
}

/** Ofis seçici: arama + çoklu seçim. `selected` null ise hepsi seçili sayılır. */
function OfficePicker({ locations, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const norm = (t) => (t || '').toLocaleLowerCase('tr-TR');
  const visible = locations.filter((l) => norm(l.title || l.locationId).includes(norm(q)));
  const isOn = (id) => !selected || selected.has(id);
  const count = selected ? selected.size : locations.length;

  const toggle = (id) => {
    const next = new Set(selected || locations.map((l) => l.locationId));
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next.size === locations.length ? null : next);
  };
  const setMany = (ids, on) => {
    const next = new Set(selected || locations.map((l) => l.locationId));
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    onChange(next.size === locations.length ? null : next);
  };

  return (
    <div className="relative" ref={ref}>
      <span className="block text-xs text-gray-500">Ofis</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-1 inline-flex items-center gap-2 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white hover:bg-gray-50 min-w-[190px] justify-between"
      >
        <span>{selected ? `${nfmt.format(count)} / ${nfmt.format(locations.length)} ofis` : `Tüm ofisler (${nfmt.format(locations.length)})`}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-80 rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-gray-200">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ofis ara (ör. havalimanı, İzmir)"
                className="flex-1 text-sm outline-none bg-transparent"
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs">
              <button type="button" className="text-blue-600 hover:underline"
                onClick={() => (q ? setMany(visible.map((l) => l.locationId), true) : onChange(null))}>
                {q ? `Görünenleri seç (${visible.length})` : 'Tümünü seç'}
              </button>
              <button type="button" className="text-gray-500 hover:underline"
                onClick={() => (q ? setMany(visible.map((l) => l.locationId), false) : onChange(new Set()))}>
                {q ? 'Görünenleri kaldır' : 'Temizle'}
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {visible.map((l) => (
              <button
                type="button"
                key={l.locationId}
                onClick={() => toggle(l.locationId)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50"
              >
                <span className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${isOn(l.locationId) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                  {isOn(l.locationId) && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className={l.missing ? 'text-gray-400' : 'text-gray-800'}>{l.title || l.locationId}</span>
              </button>
            ))}
            {!visible.length && <div className="px-3 py-3 text-sm text-gray-400">Eşleşen ofis yok</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Az yorumlu ay yanıltıcıdır: 1 yorumla gelen 5,0 ile 90 yorumla gelen 4,8 aynı ağırlıkta okunmasın. */
const THIN = 3;

function Trend({ t }) {
  if (t === 'up') return <ArrowUp className="w-3.5 h-3.5 text-emerald-600" />;
  if (t === 'down') return <ArrowDown className="w-3.5 h-3.5 text-rose-500" />;
  return <Minus className="w-3.5 h-3.5 text-gray-300" />;
}

export default function GbpScoreMatrix({ brandId, brandName, group = null, groupLabel = null, onGroups }) {
  const [months, setMonths] = useState(12);
  const [selected, setSelected] = useState(null); // null = tüm ofisler
  const [pdfBusy, setPdfBusy] = useState(false);

  // Grup (Europcar/Goldcar) değişince ofis listesi değişir; eski seçim anlamsız kalır.
  useEffect(() => { setSelected(null); }, [group, brandId]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get('/gbp-brand/review-matrix', {
        params: { months, ...(group ? { group } : {}) },
        headers: { 'X-Brand-ID': brandId },
      });
      setData(r.data);
      onGroups?.(r.data?.groups || [], r.data?.group || null);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, months, group]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  /** Tüm yorumları baştan okur — 82 lokasyonda ~30 sn sürer, cron her gece 04:10'da koşar. */
  const refreshNow = async () => {
    setRefreshing(true);
    setErr(null);
    try {
      await api.post('/gbp-brand/review-matrix/refresh', {}, {
        headers: { 'X-Brand-ID': brandId }, timeout: 15 * 60 * 1000,
      });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const axis = data?.months || [];
  const allLocations = data?.locations || [];
  const locations = useMemo(
    () => (selected ? allLocations.filter((l) => selected.has(l.locationId)) : allLocations),
    [allLocations, selected],
  );
  const total = useMemo(
    () => (data ? (selected ? subsetTotal(locations) : data.total) : null),
    [data, selected, locations],
  );

  /** Excel iki sayfa: puanlar ve arkasındaki yorum sayıları. Puan tek başına "kaç yorumdan" sorusunu bırakır. */
  const exportExcel = () => {
    if (!locations.length) return;
    const sheet = (pick) => locations.map((l) => {
      const row = {
        'Lokasyon': l.title || l.locationId,
        'Genel Google Skoru': l.rating != null ? Number(l.rating.toFixed(1)) : '',
        'Toplam Yorum': l.reviewCount ?? '',
      };
      for (const m of axis) row[monthShort(m)] = pick(l.months[m]);
      row['Not'] = l.missing ? 'Google kaydı bizim erişimimizde değil' : (l.reviewCount ? '' : 'Henüz yorum yok');
      return row;
    }).concat([(() => {
      const t = total;
      const row = {
        'Lokasyon': 'TOPLAM',
        'Genel Google Skoru': t.rating != null ? Number(t.rating.toFixed(2)) : '',
        'Toplam Yorum': t.reviewCount ?? '',
      };
      for (const m of axis) row[monthShort(m)] = pick(t.months[m]);
      return row;
    })()]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb,
      XLSX.utils.json_to_sheet(sheet((c) => (c ? Number(c.avg.toFixed(2)) : ''))), 'Aylık Puan');
    XLSX.utils.book_append_sheet(wb,
      XLSX.utils.json_to_sheet(sheet((c) => (c ? c.reviews : ''))), 'Gelen Yorum');
    // Ekrandaki gibi: her ayın puanı ve yanında o ay gelen yorum sayısı
    const both = [...locations, { title: 'TOPLAM', ...total }].map((l) => {
      const row = {
        'Lokasyon': l.title || l.locationId,
        'Genel Google Skoru': l.rating != null ? Number(l.rating.toFixed(l.title === 'TOPLAM' ? 2 : 1)) : '',
        'Toplam Yorum': l.reviewCount ?? '',
      };
      for (const m of axis) {
        const c = l.months[m];
        row[`${monthShort(m)} Puan`] = c ? Number(c.avg.toFixed(2)) : '';
        row[`${monthShort(m)} Yorum`] = c ? c.reviews : '';
      }
      return row;
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(both), 'Puan + Yorum');
    const slug = (brandName || 'marka').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    XLSX.writeFile(wb, `gbp-puan-matrisi-${slug}-${axis[0]}_${axis[axis.length - 1]}.xlsx`);
  };

  /** PDF: ekrandaki matrisin aynısı (renkli hücre + altında yorum sayısı), seçili ofislerle. */
  const exportPdf = async () => {
    if (!locations.length || pdfBusy) return;
    setPdfBusy(true);
    try {
      const [reg, semi] = await loadInter();
      const wide = axis.length > 12;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: wide ? 'a3' : 'a4' });
      doc.addFileToVFS('Inter-Regular.ttf', reg);
      doc.addFont('Inter-Regular.ttf', 'Inter', 'normal');
      doc.addFileToVFS('Inter-SemiBold.ttf', semi);
      doc.addFont('Inter-SemiBold.ttf', 'Inter', 'bold');
      doc.setFont('Inter', 'normal');

      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 28;
      const title = ['Google Puan Matrisi', brandName, groupLabel].filter(Boolean).join(' · ');
      const scope = selected ? `${nfmt.format(locations.length)} ofis seçili` : `Tüm ofisler (${nfmt.format(locations.length)})`;

      doc.setFont('Inter', 'bold'); doc.setFontSize(15); doc.setTextColor(17, 24, 39);
      doc.text(title, M, M + 10);
      doc.setFont('Inter', 'normal'); doc.setFontSize(8.5); doc.setTextColor(107, 114, 128);
      doc.text(`${monthLong(axis[0])} – ${monthLong(axis[axis.length - 1])}  ·  ${scope}  ·  Oluşturma: ${new Date().toLocaleDateString('tr-TR')}`, M, M + 26);
      doc.text('Ay hücreleri o ay gelen yorumların ortalamasıdır; genel skor Google’ın gösterdiği kümülatif puandır.', M, M + 38);

      const cell = (c) => (c ? `${r1(c.avg)}\n${nfmt.format(c.reviews)} yorum` : '—');
      const body = locations.map((l) => [
        `${l.title || l.locationId}\n${l.missing ? 'Google kaydı erişimimizde değil' : l.reviewCount ? `${nfmt.format(l.reviewCount)} yorum` : 'henüz yorum yok'}`,
        l.missing ? '' : r1(l.rating),
        ...axis.map((m) => cell(l.months[m])),
      ]);
      const foot = [[
        `TOPLAM\n${nfmt.format(total.locations || locations.length)} lokasyon · ${nfmt.format(total.reviewCount || 0)} yorum`,
        r2(total.rating),
        ...axis.map((m) => { const c = total.months[m]; return c ? `${r2(c.avg)}\n${nfmt.format(c.reviews)} yorum` : '—'; }),
      ]];

      const nameW = wide ? 190 : 150;
      const scoreW = 44;
      const monthW = (W - 2 * M - nameW - scoreW) / axis.length;
      const colStyles = { 0: { cellWidth: nameW, halign: 'left' }, 1: { cellWidth: scoreW } };
      axis.forEach((_, i) => { colStyles[i + 2] = { cellWidth: monthW }; });

      autoTable(doc, {
        startY: M + 50,
        margin: { left: M, right: M, top: M, bottom: 34 },
        head: [['Lokasyon', 'Genel skor', ...axis.map(monthShort)]],
        body,
        foot,
        showFoot: 'lastPage',
        theme: 'grid',
        styles: {
          font: 'Inter', fontSize: 7, cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
          halign: 'center', valign: 'middle', lineColor: [229, 231, 235], lineWidth: 0.4, textColor: [31, 41, 55],
        },
        headStyles: { font: 'Inter', fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [107, 114, 128], fontSize: 7 },
        footStyles: { font: 'Inter', fontStyle: 'bold', fillColor: [243, 244, 246], textColor: [17, 24, 39], fontSize: 7 },
        columnStyles: colStyles,
        didParseCell: (d) => {
          if (d.section === 'head' && d.column.index === 0) d.cell.styles.halign = 'left';
          if (d.section === 'foot' && d.column.index === 0) d.cell.styles.halign = 'left';
          if (d.section !== 'body') return;
          const l = locations[d.row.index];
          if (d.column.index === 0 && l.missing) d.cell.styles.textColor = [156, 163, 175];
          if (d.column.index === 1) d.cell.styles.fontStyle = 'bold';
          if (d.column.index < 2) return;
          const c = l.months[axis[d.column.index - 2]];
          const sc = pdfScale(c?.avg);
          if (sc.fill) d.cell.styles.fillColor = sc.fill;
          d.cell.styles.textColor = sc.text;
          if (c && c.reviews < THIN) d.cell.styles.fontStyle = 'normal';
        },
      });

      // Lejant + sayfa numarası her sayfada
      const legendItems = [
        [[4, 120, 87], '5,0 Mükemmel'], [[209, 250, 229], '4,5 – 4,9 Çok İyi'], [[254, 243, 199], '3,0 – 4,4 Orta'],
        [[254, 215, 170], '2,0 – 2,9 Zayıf'], [[244, 63, 94], '1,0 – 1,9 Çok Zayıf'],
      ];
      const pages = doc.getNumberOfPages();
      for (let p = 1; p <= pages; p += 1) {
        doc.setPage(p);
        let x = M;
        const y = H - 18;
        doc.setFont('Inter', 'normal'); doc.setFontSize(7); doc.setTextColor(75, 85, 99);
        for (const [rgb, label] of legendItems) {
          doc.setFillColor(...rgb); doc.roundedRect(x, y - 6.5, 8, 8, 1.5, 1.5, 'F');
          doc.text(label, x + 11, y);
          x += doc.getTextWidth(label) + 24;
        }
        doc.setTextColor(156, 163, 175);
        doc.text('— Veri yok  ·  Kaynak: Google Business Profile, Glowytics', x, y);
        doc.text(`${p} / ${pages}`, W - M, y, { align: 'right' });
      }

      const slug = [brandName, groupLabel].filter(Boolean).join('-').replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'marka';
      doc.save(`gbp-puan-matrisi-${slug}-${axis[0]}_${axis[axis.length - 1]}.pdf`);
    } catch (e) {
      setErr(`PDF oluşturulamadı: ${e.message}`);
    } finally {
      setPdfBusy(false);
    }
  };

  const empty = !loading && !err && !allLocations.length;

  const legend = useMemo(() => ([
    ['bg-emerald-700 text-white', '5,0 Mükemmel'],
    ['bg-emerald-100 text-emerald-900', '4,5 – 4,9 Çok İyi'],
    ['bg-amber-100 text-amber-900', '3,0 – 4,4 Orta'],
    ['bg-orange-200 text-orange-900', '2,0 – 2,9 Zayıf'],
    ['bg-rose-500 text-white', '1,0 – 1,9 Çok Zayıf'],
  ]), []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-500">
          Dönem
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="block mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white"
          >
            {[6, 12, 18, 24].map((n) => <option key={n} value={n}>Son {n} ay</option>)}
          </select>
        </label>
        {!!allLocations.length && (
          <OfficePicker locations={allLocations} selected={selected} onChange={setSelected} />
        )}
        <button
          onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </button>
        <button
          onClick={exportExcel} disabled={!locations.length}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> Excel indir
        </button>
        <button
          onClick={exportPdf} disabled={!locations.length || pdfBusy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-40"
        >
          {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF indir
        </button>
        <button
          onClick={refreshNow} disabled={refreshing}
          title="Tüm yorumları Google'dan baştan okur (~30 sn). Cron her gece 04:10'da kendisi koşar."
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {refreshing ? 'Yorumlar okunuyor…' : 'Google’dan tazele'}
        </button>
      </div>

      <p className="text-xs text-gray-500 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Ay hücreleri <strong>o ay gelen yorumların</strong> ortalamasıdır; soldaki genel
          skor Google’ın gösterdiği kümülatif puandır. Genel skor binlerce yorumun
          ağırlığıyla neredeyse hiç kıpırdamaz, aylık karne ise gidişatı gösterir.
          {data?.refreshedAt && (
            <> Son hesaplama: {new Date(data.refreshedAt).toLocaleString('tr-TR')}.</>
          )}
        </span>
      </p>

      {err && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Matris hazırlanıyor…
        </div>
      )}

      {empty && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Matris henüz hesaplanmadı.</div>
            <p className="mt-1 text-amber-700">
              “Google’dan tazele” ile yorumlar baştan okunur; geçmiş aylar yorum
              tarihlerinden hesaplandığı için tablo ilk çalıştırmada dolu gelir.
            </p>
          </div>
        </div>
      )}

      {!loading && !!allLocations.length && !locations.length && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Hiç ofis seçili değil. Yukarıdaki “Ofis” listesinden en az bir ofis seçin.
        </div>
      )}

      {!loading && !!locations.length && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="sticky left-0 z-10 bg-gray-50 text-left font-medium px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 min-w-[260px]">
                    Lokasyon
                  </th>
                  <th className="px-3 py-2.5 text-[11px] uppercase tracking-wide text-gray-500 font-medium whitespace-nowrap">
                    Genel Google Skoru
                  </th>
                  {axis.map((m) => (
                    <th key={m} className="px-2 py-2.5 text-[11px] font-medium text-gray-500 whitespace-nowrap">
                      {monthShort(m)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {locations.map((l) => (
                  <tr key={l.locationId} className="border-b border-gray-50 last:border-0 group">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50/80 px-4 py-2 min-w-[260px]">
                      <div className={l.missing ? 'text-gray-400' : 'text-gray-900'}>{l.title || l.locationId}</div>
                      <div className="text-[11px] text-gray-400">
                        {l.missing
                          ? 'Google kaydı erişimimizde değil'
                          : l.reviewCount ? `${nfmt.format(l.reviewCount)} yorum` : 'henüz yorum yok'}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {!l.missing && (
                      <div className="flex items-center justify-center gap-1.5">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                        <span className="font-medium text-gray-900 tabular-nums">{r1(l.rating)}</span>
                        <span title={l.trend
                          ? `Son 3 ayda gelen yorumların ortalaması ${r1(l.recent?.avg)} — genel puana göre ${l.trend === 'up' ? 'daha iyi' : l.trend === 'down' ? 'daha kötü' : 'aynı'}`
                          : 'Son 3 ayda yorum gelmemiş'}>
                          <Trend t={l.trend} />
                        </span>
                      </div>
                      )}
                    </td>
                    {axis.map((m) => {
                      const c = l.months[m];
                      const s = scale(c?.avg);
                      const thin = c && c.reviews < THIN;
                      return (
                        <td key={m} className="px-1.5 py-1.5 text-center">
                          <div
                            title={c
                              ? `${monthLong(m)} · ${nfmt.format(c.reviews)} yorum · ortalama ${r2(c.avg)}${thin ? ' (az yorum — tesadüfe açık)' : ''}`
                              : `${monthLong(m)} · yorum gelmedi`}
                            className={`rounded-md px-2 py-1 text-[13px] tabular-nums ${s.cls} ${thin ? 'opacity-60 italic' : ''}`}
                          >
                            {c ? r1(c.avg) : '—'}
                          </div>
                          {c && (
                            <div className="mt-0.5 text-[10px] leading-none text-gray-400 tabular-nums whitespace-nowrap">
                              {nfmt.format(c.reviews)} yorum
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="bg-gray-50 border-t border-gray-200 font-medium">
                  <td className="sticky left-0 z-10 bg-gray-50 px-4 py-2">
                    <div className="text-gray-900">TOPLAM</div>
                    <div className="text-[11px] text-gray-400">
                      {nfmt.format(total.locations || locations.length)} lokasyon ·{' '}
                      {nfmt.format(total.reviewCount || 0)} yorum
                      {total.missing ? ` · ${nfmt.format(total.missing)} istasyon erişimde değil` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums text-gray-900">{r2(total.rating)}</td>
                  {axis.map((m) => {
                    const c = total.months[m];
                    return (
                      <td key={m} className="px-1.5 py-1.5 text-center tabular-nums text-gray-900"
                        title={c ? `${monthLong(m)} · ${nfmt.format(c.reviews)} yorum · ortalama ${r2(c.avg)}` : `${monthLong(m)} · yorum gelmedi`}>
                        <div>{c ? r2(c.avg) : '—'}</div>
                        {c && (
                          <div className="mt-0.5 text-[10px] leading-none font-normal text-gray-500 whitespace-nowrap">
                            {nfmt.format(c.reviews)} yorum
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 bg-gray-50/70 border-t border-gray-200 text-[11px] text-gray-600">
            {legend.map(([cls, label]) => (
              <span key={label} className="inline-flex items-center gap-1.5">
                <span className={`inline-block w-4 h-4 rounded ${cls}`} />
                {label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 text-gray-400">— Veri Yok</span>
            <span className="inline-flex items-center gap-1.5 italic opacity-70">soluk hücre: {THIN}’ten az yorum</span>
          </div>
        </div>
      )}

      {!loading && !!locations.length && (
        <p className="text-xs text-gray-400">
          TOPLAM satırındaki aylık puan, lokasyon ortalamalarının düz ortalaması değil
          o ay gelen tüm yorumların ortalamasıdır. Gidişat oku son 3 ayda gelen
          yorumların ortalamasını genel puanla karşılaştırır.
        </p>
      )}
    </div>
  );
}
